import { shouldRetryProxyResponse } from './proxyRetryPolicy.js';

const PROXY_UNAVAILABLE_COOLDOWN_MS = 30_000;

export function normalizeFalProxyUrl(value) {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\/+$/, '');
}

function isRetryableProxyFetchError(error) {
  if (!error || error?.name === 'AbortError') return false;
  if (error instanceof TypeError) return true;

  const message = String(error?.message || '').toLowerCase();
  return message.includes('failed to fetch')
    || message.includes('fetch failed')
    || message.includes('networkerror')
    || message.includes('load failed');
}

function canReplayProxyRequest(input, init) {
  if (!(typeof input === 'string' || input instanceof URL)) return false;
  if (!init || !('body' in init)) return true;
  return typeof init.body === 'string' || init.body == null;
}

function resolveProxyRequestTarget(inputUrl, proxyUrls = []) {
  const normalizedInputUrl = normalizeFalProxyUrl(inputUrl);
  if (!normalizedInputUrl) return null;

  for (const proxyUrl of proxyUrls) {
    if (normalizedInputUrl === proxyUrl) {
      return { proxyUrl, pathSuffix: '' };
    }
    if (normalizedInputUrl.startsWith(`${proxyUrl}/`)) {
      return {
        proxyUrl,
        pathSuffix: normalizedInputUrl.slice(proxyUrl.length),
      };
    }
  }
  return null;
}

function prioritizeReachableProxyTargets(
  targets = [],
  unavailableProxyTargets = new Map(),
  now = Date.now(),
) {
  const availableTargets = [];
  const coolingTargets = [];

  for (const target of targets) {
    const unavailableUntil = unavailableProxyTargets.get(target) || 0;
    if (unavailableUntil > now) coolingTargets.push(target);
    else availableTargets.push(target);
  }

  return availableTargets.length === 0
    ? targets
    : [...availableTargets, ...coolingTargets];
}

export function createFalProxyFetch(proxyUrls = [], baseFetch = globalThis.fetch) {
  const normalizedProxyUrls = proxyUrls
    .map(normalizeFalProxyUrl)
    .filter(Boolean);
  const unavailableProxyTargets = new Map();

  return async (input, init) => {
    const inputUrl = typeof input === 'string' ? input : input?.url;
    const matchedProxyRequest = resolveProxyRequestTarget(inputUrl, normalizedProxyUrls);
    if (!matchedProxyRequest) return baseFetch(input, init);

    const targets = [
      matchedProxyRequest.proxyUrl,
      ...normalizedProxyUrls.filter((url) => url !== matchedProxyRequest.proxyUrl),
    ].map((proxyUrl) => `${proxyUrl}${matchedProxyRequest.pathSuffix}`);
    const prioritizedTargets = prioritizeReachableProxyTargets(targets, unavailableProxyTargets);
    const replayable = canReplayProxyRequest(input, init);
    let lastError = null;

    for (let index = 0; index < prioritizedTargets.length; index += 1) {
      const target = prioritizedTargets[index];
      try {
        const response = await baseFetch(target, init);
        if (
          !replayable
          || index === prioritizedTargets.length - 1
          || !(await shouldRetryProxyResponse(response, target))
        ) {
          unavailableProxyTargets.delete(target);
          return response;
        }

        unavailableProxyTargets.set(target, Date.now() + PROXY_UNAVAILABLE_COOLDOWN_MS);
        console.warn(
          `[fal] Proxy ${target} returned ${response.status}. Retrying ${prioritizedTargets[index + 1]}.`,
        );
      } catch (error) {
        lastError = error;
        if (
          !replayable
          || !isRetryableProxyFetchError(error)
          || index === prioritizedTargets.length - 1
        ) {
          throw error;
        }
        unavailableProxyTargets.set(target, Date.now() + PROXY_UNAVAILABLE_COOLDOWN_MS);
        console.warn(`[fal] Failed to reach ${target}. Retrying ${prioritizedTargets[index + 1]}.`);
      }
    }

    throw lastError || new Error('Failed to reach FAL proxy.');
  };
}
